import { useMemo } from 'react';

import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsProvider';
import { TTeamWiseEmployeeData } from '../../../types/analyticsTypes';
import { countWorkingDays } from '../../../utils';
import { calculateDateRanges } from '../../../utils/dateUtils';
import { mapCheckIns, mapLeaves } from '../../../utils/mappingUtils';

import { useGetAggregatedCheckinsData } from './useGetAggregatedCheckinsData';

export const useGetCheckInsData = (): {
  teamWiseUserAnalyticData: {
    currentPeriod: TTeamWiseEmployeeData;
    previousPeriod: TTeamWiseEmployeeData;
    todayOooMap: { [key: string]: boolean };
  };
  isLoading: boolean;
  error: Error | null;
} => {
  const { selectedPeriod } = useAnalytics();
  const dateRange = useMemo(() => {
    return calculateDateRanges(selectedPeriod.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPeriod)]);
  const {
    data: { aggregatedCheckIns, leaves } = {
      aggregatedCheckIns: [],
      leaves: [],
    },
    isLoading,
    error,
  } = useGetAggregatedCheckinsData(
    dateRange.thisPeriodStart,
    dateRange.thisPeriodEnd,
  );
  const {
    data: {
      aggregatedCheckIns: previousAggregatedCheckIns,
      leaves: previousLeaves,
    } = {
      previousAggregatedCheckIns: [],
      previousLeaves: [],
    },
    isLoading: previousIsLoading,
    error: previousError,
  } = useGetAggregatedCheckinsData(
    dateRange.lastPeriodStart,
    dateRange.lastPeriodEnd,
  );
  if (error || previousError) {
    return {
      teamWiseUserAnalyticData: {
        currentPeriod: {},
        previousPeriod: {},
        todayOooMap: {},
      },
      isLoading: false,
      error,
    };
  }

  const { leavesMap, todayOooMap } = mapLeaves(
    leaves || [],
    dateRange.thisPeriodStart,
    dateRange.thisPeriodEnd,
  );
  const { leavesMap: previousLeavesMap } = mapLeaves(
    previousLeaves || [],
    dateRange.lastPeriodStart,
    dateRange.lastPeriodEnd,
  );
  const individualCheckIns = mapCheckIns(
    aggregatedCheckIns || [],
    leavesMap,
    todayOooMap,
    countWorkingDays(dateRange.thisPeriodStart, dateRange.thisPeriodEnd),
  );
  const previousIndividualCheckIns = mapCheckIns(
    previousAggregatedCheckIns || [],
    previousLeavesMap,
    {},
    countWorkingDays(dateRange.lastPeriodStart, dateRange.lastPeriodEnd),
  );
  return {
    teamWiseUserAnalyticData: {
      currentPeriod: individualCheckIns,
      previousPeriod: previousIndividualCheckIns,
      todayOooMap,
    },
    isLoading: isLoading || previousIsLoading,
    error: error || previousError,
  };
};
